import React, { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import type { ConnectionState, TranscriptMessage, QCallBaseConfig } from '../types';

/**
 * Widget state interface
 */
export interface WidgetState {
  isOpen: boolean;
  connectionState: ConnectionState;
  isMuted: boolean;
  transcripts: TranscriptMessage[];
  error: string | null;
}

/**
 * Widget context value
 */
export interface WidgetContextValue extends WidgetState {
  config: QCallBaseConfig;
  open: () => void;
  close: () => void;
  toggle: () => void;
  setConnectionState: (state: ConnectionState) => void;
  setMuted: (muted: boolean) => void;
  addTranscript: (message: Omit<TranscriptMessage, 'id' | 'timestamp'>) => void;
  clearTranscripts: () => void;
  setError: (error: string | null) => void;
}

const WidgetContext = createContext<WidgetContextValue | null>(null);

/**
 * Widget provider props
 */
export interface WidgetProviderProps {
  children: ReactNode;
  config: QCallBaseConfig;
  initialState?: Partial<WidgetState>;
}

/**
 * Widget context provider
 */
export function WidgetProvider({ children, config, initialState }: WidgetProviderProps) {
  const [state, setState] = useState<WidgetState>({
    isOpen: initialState?.isOpen ?? config.autoOpen ?? false,
    connectionState: initialState?.connectionState ?? 'idle',
    isMuted: initialState?.isMuted ?? false,
    transcripts: initialState?.transcripts ?? [],
    error: initialState?.error ?? null,
  });

  const open = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: true }));
  }, []);

  const close = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const toggle = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: !prev.isOpen }));
  }, []);

  const setConnectionState = useCallback((connectionState: ConnectionState) => {
    setState((prev) => ({ ...prev, connectionState }));
  }, []);

  const setMuted = useCallback((isMuted: boolean) => {
    setState((prev) => ({ ...prev, isMuted }));
  }, []);

  const addTranscript = useCallback(
    (message: Omit<TranscriptMessage, 'id' | 'timestamp'>) => {
      const newMessage: TranscriptMessage = {
        ...message,
        id: `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        timestamp: new Date(),
      };
      setState((prev) => ({
        ...prev,
        transcripts: [...prev.transcripts, newMessage],
      }));
    },
    []
  );

  const clearTranscripts = useCallback(() => {
    setState((prev) => ({ ...prev, transcripts: [] }));
  }, []);

  const setError = useCallback((error: string | null) => {
    setState((prev) => ({ ...prev, error }));
  }, []);

  const value: WidgetContextValue = {
    ...state,
    config,
    open,
    close,
    toggle,
    setConnectionState,
    setMuted,
    addTranscript,
    clearTranscripts,
    setError,
  };

  return <WidgetContext.Provider value={value}>{children}</WidgetContext.Provider>;
}

/**
 * Hook to access widget context
 */
export function useWidget(): WidgetContextValue {
  const context = useContext(WidgetContext);
  if (!context) {
    throw new Error('useWidget must be used within a WidgetProvider');
  }
  return context;
}

/**
 * Export the context for advanced usage
 */
export { WidgetContext };
